import { useEffect, useState } from "react";
import { createDevSuggestion, api } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sparkles, RefreshCw, Send, X, BookOpen, Briefcase, Award, Users, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = {
  course: BookOpen,
  project: Briefcase,
  certification: Award,
  coaching: Users,
  book: BookOpen,
};

const PRIORITY_BADGE = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function ProactiveSuggestionsWidget({ suggesterId, scope = "all" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [dismissed, setDismissed] = useState(new Set());

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const resp = await api.get(`/proactive-suggestions/${suggesterId}`, {
        params: { scope, ...(refresh ? { refresh: true } : {}) },
      });
      setData(resp.data);
      if (refresh) toast.success("Yeniden üretildi", { description: "AI çalışan sinyallerini taradı." });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (suggesterId) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggesterId, scope]);

  const handleSend = async (item) => {
    setSending((p) => ({ ...p, [item.employee_id + item.title]: true }));
    try {
      await createDevSuggestion({
        employee_id: item.employee_id,
        suggested_by_id: suggesterId,
        skill_name: item.skill_name,
        resource_type: item.resource_type,
        title: item.title,
        provider: item.provider,
        duration_weeks: item.duration_weeks || 6,
        message: `${item.rationale} (AI taslağıyla gönderildi)`,
        priority: item.priority || "medium",
      });
      toast.success("Öneri gönderildi", { description: `${item.employee_name} → ${item.title}` });
      setDismissed((s) => new Set([...s, item.employee_id + item.title]));
    } catch {
      toast.error("Gönderilemedi");
    } finally {
      setSending((p) => ({ ...p, [item.employee_id + item.title]: false }));
    }
  };

  const handleDismiss = (item) => {
    setDismissed((s) => new Set([...s, item.employee_id + item.title]));
  };

  const visible = (data?.suggestions || []).filter((s) => !dismissed.has(s.employee_id + s.title));

  return (
    <div
      className="card-surface p-6 bg-gradient-to-br from-violet-50/60 via-white to-white border-violet-200"
      data-testid="proactive-suggestions-widget"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-300 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <div className="label-eyebrow text-violet-700 mb-1">AI Proaktif Öneri Tetikleyici</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">
              Şu hafta önermeye değer{visible.length > 0 ? <> <span className="text-violet-700">{visible.length} gelişim önerisi</span></> : " gelişim önerisi yok"}
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Plena AI haftalık olarak skill gap × performans × bağlılık × kariyer hedefi sinyallerini birleştirir ve
              kim için hangi kaynağın anlamlı olduğunu önerir. Tek tıkla gönderebilirsin.
            </p>
          </div>
        </div>
        <Button
          onClick={() => load(true)}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="proactive-refresh-btn"
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>

      {loading && !data && (
        <div className="space-y-2.5">
          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-1/2" />
          <div className="h-3 bg-slate-100 rounded animate-pulse w-3/4" />
          <div className="text-[0.65rem] text-slate-500 italic mt-2">Plena AI çalışan sinyallerini tarıyor…</div>
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="text-center py-8" data-testid="proactive-empty">
          <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Tüm öneriler işlendi veya bu kapsamda kritik gap yok.</p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="space-y-3">
          {visible.map((s, idx) => {
            const Icon = TYPE_ICON[s.resource_type] || BookOpen;
            const key = s.employee_id + s.title;
            return (
              <div key={idx} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`proactive-row-${idx}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarImage src={s.employee_avatar} />
                    <AvatarFallback>{s.employee_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-medium text-slate-900">{s.employee_name}</span>
                      <span className="text-xs text-slate-500">· {s.employee_title}</span>
                      <Badge variant="outline" className={`text-[0.65rem] ${PRIORITY_BADGE[s.priority] || PRIORITY_BADGE.medium}`}>
                        {s.priority === "high" ? "Yüksek öncelik" : s.priority === "low" ? "Düşük" : "Orta"}
                      </Badge>
                    </div>
                    <div className="flex items-start gap-2 mb-2">
                      <Icon className="w-4 h-4 text-violet-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900 leading-snug">{s.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {s.provider} · {s.duration_weeks || 6} hafta · <span className="text-violet-700">{s.skill_name}</span>
                        </div>
                      </div>
                    </div>
                    {s.rationale && (
                      <div className="text-xs text-slate-600 italic bg-violet-50/40 border-l-2 border-violet-300 px-2.5 py-1.5 rounded-r">
                        <Sparkles className="w-3 h-3 inline mr-1 text-violet-600" />
                        {s.rationale}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      onClick={() => handleSend(s)}
                      disabled={sending[key]}
                      size="sm"
                      data-testid={`proactive-send-${idx}`}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" /> {sending[key] ? "Gönderiliyor…" : "Bu öneriyi gönder"}
                    </Button>
                    <Button
                      onClick={() => handleDismiss(s)}
                      variant="ghost"
                      size="sm"
                      data-testid={`proactive-dismiss-${idx}`}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Şimdi değil
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
